import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { newestFirst } from "@/utils/sortHistory";
import BoardWorkspace from "@/components/BoardWorkspace";
import { InformationalStatusText, formatInformationalStatus } from "@/components/InformationalStatusText";
import { TypedDateInput } from "@/components/TypedDateInput";
import { LocateReadinessLabel, PermitReadinessLabel, todayISO } from "@/components/PermitCalculatedBadge";
import { useTimeRefresh } from "@/hooks/useTimeRefresh";
import { formatBusinessDate, formatLocateExpiration } from "@/utils/datetime";
import LocateDeadlineInput from "@/components/LocateDeadlineInput";
import HistoricalLocateCard from "@/components/HistoricalLocateCard";
import { getProjectModuleAvailability, isProjectRecordAvailable, projectRecordModule } from "@/utils/projectFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const LOCATE_CONDITIONS = ["valid", "trouble", "remark"];
const emptyForm = {
  project_id: "",
  permit_type: "Permit",
  reference_number: "",
  request_date: "",
  expires_on: "",
  expires_at: "",
  received_at: "",
  received_at_source: "",
  does_not_expire: false,
  status: "missing",
  notes: "",
  ticket_not_needed: false,
  ticket_description: "",
  active: true,
};

const newestRecordsFirst = newestFirst;

export default function PermitsLocatesPage() {
  const { canManageRecords, user } = useAuth();
  const features = useMemo(() => user?.features || {}, [user?.features]);
  const modules = useMemo(() => getProjectModuleAvailability(features), [features]);
  const [searchParams] = useSearchParams();
  const targetPermitId = searchParams.get("permit_id") || "";
  const [allActiveRecords, setActiveRecords] = useState([]);
  const [allHistoryRecords, setHistoryRecords] = useState([]);
  const activeRecords = useMemo(() => allActiveRecords.filter((record) => isProjectRecordAvailable(record, features)), [allActiveRecords, features]);
  const historyRecords = useMemo(() => allHistoryRecords.filter((record) => isProjectRecordAvailable(record, features)), [allHistoryRecords, features]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(() => ({ ...emptyForm, permit_type: modules.permits ? "Permit" : "Locate / 811" }));
  const [editingId, setEditingId] = useState("");
  const [historyCorrection, setHistoryCorrection] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const [timing, setTiming] = useState(null);
  const load = useCallback(async () => {
    if (!modules.any) return;
    const [open, history] = await Promise.all([
      axios.get(`${API}/permits-locates`, { params: { filter: "open" } }),
      axios.get(`${API}/permits-locates`, { params: { filter: "history" } }),
    ]);
    setActiveRecords(open.data.records || []);
    setHistoryRecords(history.data.records || []);
    setTiming(open.data);
  }, [modules]);
  useTimeRefresh(load, timing);
  const loadProjects = useCallback(async () => {
    const response = await axios.get(`${API}/bootstrap`);
    setProjects(response.data.projects || []);
  }, []);
  useEffect(() => {
    load().catch(() => toast.error("Permits and locates could not be loaded."));
  }, [load]);
  useEffect(() => {
    loadProjects().catch(() => toast.error("Projects could not be loaded."));
  }, [loadProjects]);
  useEffect(() => {
    if (modules.any && !isProjectRecordAvailable(form, features)) {
      setForm({ ...emptyForm, permit_type: modules.permits ? "Permit" : "Locate / 811" });
      setEditingId(""); setHistoryCorrection(false); setCorrectionReason("");
    }
  }, [modules, features, form]);
  useEffect(() => {
    if (!targetPermitId) {
      return;
    }
    const target = [...activeRecords, ...historyRecords].find(
      (record) => record.id === targetPermitId,
    );
    if (!target) {
      return;
    }
    if (canManageRecords && editingId !== target.id) {
      setEditingId(target.id);
      setForm({ ...target });
      setHistoryCorrection(projectRecordModule(target) !== "permits" && target.is_historical === true);
      setCorrectionReason("");
    }
    window.requestAnimationFrame(() => {
      document
        .querySelector(canManageRecords ? `[data-testid="permit-locate-form"]` : `[data-testid="permit-record-${targetPermitId}"], [data-testid="permit-history-record-${targetPermitId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [activeRecords, canManageRecords, editingId, historyRecords, targetPermitId]);
  const save = async (event) => {
    event.preventDefault();
    const isPermitType = projectRecordModule(form) === "permits";
    // Permit: humans record the received fact; JFM calculates status. 811: keep condition.
    const conditionFields = isPermitType
      ? { received_at: form.received_at, does_not_expire: form.does_not_expire, expires_on: form.does_not_expire ? "" : form.expires_on }
      : { status: form.status, expires_at: (form.expires_at || "").trim() };
    try {
      if (editingId && historyCorrection) {
        const original = historyRecords.find((record) => record.id === editingId);
        if (!original) throw new Error("Historical ticket is no longer available. Refresh and try again.");
        const candidate = {
          reference_number: form.reference_number,
          request_date: form.request_date,
          expires_on: form.expires_on,
          expires_at: form.expires_at,
          status: form.status,
          notes: form.notes,
          ticket_not_needed: form.ticket_not_needed,
          ticket_description: form.ticket_description,
        };
        const changes = Object.fromEntries(
          Object.entries(candidate).filter(([field, value]) => (original[field] ?? "") !== (value ?? "")),
        );
        await axios.post(`${API}/permits/${editingId}/history-correction`, {
          reason: correctionReason,
          changes,
        });
      } else if (editingId)
        await axios.patch(`${API}/permits/${editingId}`, {
          permit_type: form.permit_type,
          reference_number: form.reference_number,
          request_date: form.request_date,
          expires_on: form.expires_on,
          ...conditionFields,
          notes: form.notes,
          ticket_not_needed: form.ticket_not_needed,
          ticket_description: form.ticket_description,
          active: form.active,
        });
      else await axios.post(`${API}/permits`, { ...form, ...conditionFields });
      setForm(emptyForm);
      setEditingId("");
      setHistoryCorrection(false);
      setCorrectionReason("");
      await load();
      toast.success(historyCorrection ? "Historical ticket correction audited." : "Permit / locate saved.");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || error.message || "Permit / locate could not be saved.",
      );
    }
  };
  const edit = (record) => {
    setEditingId(record.id);
    setForm({ ...record });
    setHistoryCorrection(projectRecordModule(record) !== "permits" && record.is_historical === true);
    setCorrectionReason("");
    window.requestAnimationFrame(() => {
      document.querySelector(`[data-testid="permit-locate-form"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const startEntry = (permitType) => {
    setEditingId("");
    setHistoryCorrection(false);
    setCorrectionReason("");
    setForm({ ...emptyForm, permit_type: permitType });
  };
  const close = async (record) => {
    try {
      await axios.post(`${API}/permits/${record.id}/deactivate`);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Record could not be closed.",
      );
    }
  };
  return (
    <BoardWorkspace
      eyebrow="Project records"
      headingTestId="permits-locates-heading"
      subtitle="Multiple Permit and Locate / 811 records can remain active for a project. Company Owner/Admin and Office/Admin control edits; all company users can view the operational queue."
      title={modules.label || "Modules unavailable"}
    >
      <section
        className="project-notes-panel"
        data-testid="permits-locates-workspace"
      >
        {canManageRecords && modules.any && (
          <div className="communication-filters permit-entry-actions" data-testid="permit-locate-entry-actions">
            {modules.permits && <button data-testid="add-permit-action" onClick={() => startEntry("Permit")} type="button">Add Permit</button>}
            {modules.locates && <button data-testid="add-locate-action" onClick={() => startEntry("Locate / 811")} type="button">Add Locate / 811</button>}
          </div>
        )}
        {canManageRecords && modules.any && isProjectRecordAvailable(form, features) && (
          <form
            className="field-note-form"
            data-testid="permit-locate-form"
            onSubmit={save}
          >
            <p className="permit-entry-type" data-testid="permit-entry-type">
              {historyCorrection ? `Audited correction: ${form.permit_type}` : editingId ? `Editing ${form.permit_type}` : `New ${form.permit_type}`}
            </p>
            <select
              data-testid="permit-project-select"
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              required
              value={form.project_id}
            >
              <option value="">Select project</option>
              {projects.map((project) => {
                const projectLabel =
                  project.project_code === project.name
                    ? project.project_code
                    : `${project.project_code} · ${project.name}`;
                return (
                  <option key={project.id} value={project.id}>
                    {projectLabel}
                  </option>
                );
              })}
            </select>
            <input
              data-testid="permit-reference-number"
              onChange={(e) =>
                setForm({ ...form, reference_number: e.target.value })
              }
              placeholder="Reference / ticket number"
              value={form.reference_number}
            />
            <TypedDateInput
              data-testid="permit-request-date"
              onChange={(value) => setForm({ ...form, request_date: value })}
              value={form.request_date}
            />
            <TypedDateInput
              data-testid="permit-expires-date"
              disabled={projectRecordModule(form) === "permits" && form.does_not_expire}
              onChange={(value) => setForm({ ...form, expires_on: value })}
              value={projectRecordModule(form) === "permits" && form.does_not_expire ? "" : form.expires_on}
            />
            {projectRecordModule(form) === "permits" ? (
              <div className="project-permit-received" data-testid="permit-received-block">
                <TypedDateInput
                  data-testid="permit-received-date"
                  onChange={(value) => setForm({ ...form, received_at: value })}
                  value={form.received_at}
                />
                <button
                  className="board-refresh-button project-permit-mark-received"
                  data-testid="permit-mark-received"
                  onClick={() => setForm({ ...form, received_at: todayISO() })}
                  type="button"
                >
                  Mark received today
                </button>
                <label className="master-editor-check-label">
                  <input
                    checked={Boolean(form.does_not_expire)}
                    data-testid="permit-does-not-expire"
                    onChange={(e) => setForm({ ...form, does_not_expire: e.target.checked, expires_on: e.target.checked ? "" : form.expires_on })}
                    type="checkbox"
                  />
                  <span>This permit does not expire</span>
                </label>
              </div>
            ) : (
              <select
                data-testid="permit-status-select"
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                value={form.status}
              >
                {!LOCATE_CONDITIONS.includes(form.status) && (
                  <option value={form.status}>{formatInformationalStatus(form.status)}</option>
                )}
                <option value="valid">Valid</option>
                <option value="trouble">Trouble</option>
                <option value="remark">Remark</option>
              </select>
            )}
            <textarea
              data-testid="permit-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Office notes"
              value={form.notes}
            />
            {projectRecordModule(form) !== "permits" && <LocateDeadlineInput value={form.expires_at} onChange={(value) => setForm({ ...form, expires_at: value })} />}
            {form.permit_type === "Locate / 811" && <><label className="permit-ticket-not-needed-control" htmlFor="permit-ticket-not-needed">
              <input data-testid="permit-ticket-not-needed" id="permit-ticket-not-needed" checked={form.ticket_not_needed} onChange={(e) => setForm({ ...form, ticket_not_needed: e.target.checked })} type="checkbox" />
              Locate / 811 ticket not needed
            </label>
            <textarea data-testid="permit-ticket-description" onChange={(e) => setForm({ ...form, ticket_description: e.target.value })} placeholder="What this Locate / 811 record covers or why it is not needed" value={form.ticket_description} /></>}
            {historyCorrection && (
              <label htmlFor="permit-history-correction-reason">
                Why is this historical ticket being corrected? (required, at least 10 characters)
                <textarea
                  id="permit-history-correction-reason"
                  data-testid="permit-history-correction-reason"
                  minLength={10}
                  required
                  value={correctionReason}
                  onChange={(event) => setCorrectionReason(event.target.value)}
                  placeholder="Explain the correction so it is retained with the field changes"
                />
              </label>
            )}
            <button
              className="invoice-primary-button"
              data-testid="permit-save-button"
              type="submit"
            >
              {historyCorrection ? "Save audited correction" : editingId ? "Save changes" : `Add ${form.permit_type}`}
            </button>
            {editingId && (
              <button
                data-testid="permit-cancel-edit"
                onClick={() => {
                  setEditingId("");
                  setHistoryCorrection(false);
                  setCorrectionReason("");
                  setForm(emptyForm);
                }}
                type="button"
              >
                Cancel
              </button>
            )}
          </form>
        )}
        <h2 className="workspace-section-title" data-testid="permits-current-heading">Current {modules.label.toLowerCase()}</h2>
        <div className="workspace-grid" data-testid="permits-locates-list">
          {newestRecordsFirst(
            activeRecords.filter(
              (record) => !record.is_historical,
            ),
          ).map((record) => (
            <article
              className={`project-note-entry ${record.id === targetPermitId ? "permit-record-focused" : ""}`}
              data-testid={`permit-record-${record.id}`}
              key={record.id}
            >
              <strong>
                {record.permit_type} ·{" "}
                {record.reference_number || "No reference"}
              </strong>
              <p>
                {record.project_code}
                {record.project_name !== record.project_code &&
                  ` · ${record.project_name}`}
                {" · "}
                {projectRecordModule(record) === "permits" ? `Expires ${formatBusinessDate(record.expires_on)}` : formatLocateExpiration(record)}
              </p>
              {record.ticket_not_needed && <p data-testid={`permit-ticket-not-needed-label-${record.id}`}>Ticket not needed · {record.ticket_description || "No description"}</p>}
              {projectRecordModule(record) === "permits" ? (
                <PermitReadinessLabel
                  reason={record.calculated_reason}
                  state={record.calculated_state}
                  testId={`permit-status-badge-${record.id}`}
                />
              ) : (
                <div className="permit-locate-conditions">
                  <span className="permit-locate-human" data-testid={`permit-condition-badge-${record.id}`}>
                    Condition: <InformationalStatusText label={record.status} />
                  </span>
                  <LocateReadinessLabel
                    reason={record.calculated_reason}
                    state={record.calculated_state}
                    testId={`permit-status-badge-${record.id}`}
                  />
                </div>
              )}
              <Link to={`/projects/${record.project_id}`}>Open project</Link>
              {canManageRecords && (
                <div className="communication-filters">
                  <button
                    data-testid={`permit-edit-${record.id}`}
                    onClick={() => edit(record)}
                    type="button"
                  >
                    Edit
                  </button>
                  {record.active !== false && (
                    <button
                      data-testid={`permit-close-${record.id}`}
                      onClick={() => close(record)}
                      type="button"
                    >
                      Close
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
        <h2 className="workspace-section-title" data-testid="permits-history-heading">Historical {modules.label.toLowerCase()}</h2>
        <div className="workspace-grid permits-locates-history-cards" data-testid="permits-locates-history-list">
          {newestRecordsFirst(historyRecords).map((record) => projectRecordModule(record) !== "permits" ? (
            <HistoricalLocateCard
              key={record.id}
              record={record}
              testId={`permit-history-record-${record.id}`}
              showProjectLink
              canCorrect={canManageRecords && record.project_lifecycle_state !== "closed"}
              onCorrect={() => edit(record)}
            />
          ) : (
            <article className={`project-note-entry ${record.id === targetPermitId ? "permit-record-focused" : ""}`} data-testid={`permit-history-record-${record.id}`} key={record.id}>
              <strong>{record.permit_type} · {record.reference_number || "No reference"}</strong>
              <p>{record.project_code} · {projectRecordModule(record) === "permits" ? `Expires ${formatBusinessDate(record.expires_on)}` : formatLocateExpiration(record)}</p>
              <PermitReadinessLabel state={record.calculated_state} reason={record.calculated_reason} testId={`permit-history-status-${record.id}`} />
              {record.deleted_at && (
                <p data-testid={`permit-retained-delete-${record.id}`}>
                  Deleted from active operations · retained in history
                </p>
              )}
              <Link to={`/projects/${record.project_id}`}>Open project</Link>
              {canManageRecords && record.project_lifecycle_state !== "closed" && <button data-testid={`permit-edit-history-${record.id}`} onClick={() => edit(record)} type="button">{projectRecordModule(record) === "permits" ? "Edit" : "Correct (audited)"}</button>}
            </article>
          ))}
        </div>
      </section>

    </BoardWorkspace>
  );
}


